<aside class="drilldown-panel bg-standardwhite border border-grey-300 rounded-md shadow-sm" role="region" aria-live="polite" aria-labelledby="dashboard-drilldown-title">
  <div class="flex items-start justify-between gap-3 border-b border-grey-300 p-4">
    <div>
      <h3 id="dashboard-drilldown-title">{{(drilldown?.Title || 'Widget drill-down') | customerText}}</h3>
      <p class="text-sm text-grey-900">
        @if (drilldown && !hasError()) {
          <span>{{drilldown.TotalRows}} records. Page {{drilldown.PageNumber}} of {{totalPages()}}.</span>
        }
        @if (!drilldown) {
          <span>Select a widget to view the underlying records.</span>
        }
      </p>
    </div>
    <button type="button" class="text-green-700 hover:text-green-400" aria-label="Close drill-down" (click)="close()">
      <span class="material-symbols-outlined">close</span>
    </button>
  </div>

  @if (loading) {
    <div class="p-4">
      @for (row of [1, 2, 3]; track row) {
        <div class="loading-row"></div>
      }
    </div>
  }

  @if (!loading && hasError()) {
    <div class="p-4" role="alert">
      <p class="font-semibold">This drill-down is unavailable.</p>
      <p class="text-sm text-grey-900 mt-1">{{drilldown?.ErrorMessage | customerText}}</p>
    </div>
  }

  @if (!loading && isEmpty()) {
    <div class="p-4">
      <p class="font-semibold">No matching records</p>
      <p class="text-sm text-grey-900 mt-1">There are no records for this selection and reporting period.</p>
    </div>
  }

  @if (!loading && drilldown && !hasError() && drilldown.Rows.length > 0) {
    <div class="overflow-x-auto">
      <table class="w-full table-auto drilldown-table" [class.drilldown-office-ranking-table]="isOfficeRanking()">
        <thead>
          <tr>
            @if (isOfficeRanking()) {
              <th class="text-right" [attr.aria-sort]="sortField === 'Rank' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'">
                <button type="button" class="ml-auto" (click)="sortBy('Rank')">
                  Rank
                  @if (sortMarker('Rank')) {
                    <span class="material-symbols-outlined">{{sortMarker('Rank')}}</span>
                  }
                </button>
              </th>
            }
            <th [attr.aria-sort]="sortField === 'PrimaryText' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'">
              <button type="button" (click)="sortBy('PrimaryText')">
                Record
                @if (sortMarker('PrimaryText')) {
                  <span class="material-symbols-outlined">{{sortMarker('PrimaryText')}}</span>
                }
              </button>
            </th>
            @if (showScoreColumn()) {
              <th class="text-right" [attr.aria-sort]="sortField === 'Score' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'">
                <button type="button" class="ml-auto" (click)="sortBy('Score')">
                  Score
                  @if (sortMarker('Score')) {
                    <span class="material-symbols-outlined">{{sortMarker('Score')}}</span>
                  }
                </button>
              </th>
            }
            @if (isOfficeRanking()) {
              <th class="text-right" [attr.aria-sort]="sortField === 'PreviousScore' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'">
                <button type="button" class="ml-auto" (click)="sortBy('PreviousScore')">
                  Previous
                  @if (sortMarker('PreviousScore')) {
                    <span class="material-symbols-outlined">{{sortMarker('PreviousScore')}}</span>
                  }
                </button>
              </th>
              <th class="text-right" [attr.aria-sort]="sortField === 'Movement' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'">
                <button type="button" class="ml-auto" (click)="sortBy('Movement')">
                  Movement
                  @if (sortMarker('Movement')) {
                    <span class="material-symbols-outlined">{{sortMarker('Movement')}}</span>
                  }
                </button>
              </th>
              <th [attr.aria-sort]="sortField === 'Coverage' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'">
                <button type="button" (click)="sortBy('Coverage')">
                  Coverage
                  @if (sortMarker('Coverage')) {
                    <span class="material-symbols-outlined">{{sortMarker('Coverage')}}</span>
                  }
                </button>
              </th>
            }
            @if (!showScoreColumn()) {
              <th [attr.aria-sort]="sortField === 'LocationName' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'">
                <button type="button" (click)="sortBy('LocationName')">
                  Location
                  @if (sortMarker('LocationName')) {
                    <span class="material-symbols-outlined">{{sortMarker('LocationName')}}</span>
                  }
                </button>
              </th>
              <th [attr.aria-sort]="sortField === 'DateDisplay' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'">
                <button type="button" (click)="sortBy('DateDisplay')">
                  Date
                  @if (sortMarker('DateDisplay')) {
                    <span class="material-symbols-outlined">{{sortMarker('DateDisplay')}}</span>
                  }
                </button>
              </th>
            }
            @if (showStatusColumn()) {
              <th [attr.aria-sort]="sortField === 'StatusText' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'">
                <button type="button" (click)="sortBy('StatusText')">
                  Status
                  @if (sortMarker('StatusText')) {
                    <span class="material-symbols-outlined">{{sortMarker('StatusText')}}</span>
                  }
                </button>
              </th>
            }
            <th class="text-right">Open record</th>
          </tr>
        </thead>
        <tbody>
          @for (row of drilldown.Rows; track row.RowType + '-' + row.RowID) {
            <tr>
              @if (isOfficeRanking()) {
                <td class="text-right font-semibold">{{row.Rank || '-'}}</td>
              }
              <td>
                <span class="font-semibold">{{row.PrimaryText | customerText}}</span>
                @if (rowTypeLabel(row)) {
                  <span class="block text-xs font-semibold text-green-700">{{rowTypeLabel(row) | customerText}}</span>
                }
                @if (recordDetailText(row)) {
                  <span class="block text-xs text-grey-900">{{recordDetailText(row) | customerText}}</span>
                }
                @if (recordMetaText(row)) {
                  <span class="block text-xs text-grey-900">{{recordMetaText(row) | customerText}}</span>
                }
                @if (row.Owner) {
                  <span class="block text-xs text-grey-900">Owner: {{row.Owner}}</span>
                }
              </td>
              @if (showScoreColumn()) {
                <td class="text-right font-semibold">{{scoreDisplay(row)}}</td>
              }
              @if (isOfficeRanking()) {
                <td class="text-right">{{previousScoreDisplay(row)}}</td>
                <td class="text-right">{{movementDisplay(row)}}</td>
                <td>{{(row.Coverage || '-') | customerText}}</td>
              }
              @if (!showScoreColumn()) {
                <td>{{row.LocationName}}</td>
                <td>{{row.DateDisplay}}</td>
              }
              @if (showStatusColumn()) {
                <td>
                  <span class="row-status" [ngClass]="rowStatusClass(row)" aria-hidden="true"></span>
                  <span>{{row.StatusText}}</span>
                </td>
              }
              <td class="text-right">
                @if (row.Selection && row.ActionLabel) {
                  <button type="button" class="underline text-green-700 hover:text-green-400" (click)="openSelection(row)">{{row.ActionLabel}}</button>
                }
                @if (!row.Selection && canOpenRecord(row)) {
                  <a class="underline text-green-700 hover:text-green-400" [href]="recordLinkUrl(row)">{{row.ActionLabel}}</a>
                }
                @if (!canOpenRecord(row)) {
                  <span class="text-grey-900">-</span>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <div class="flex items-center justify-between gap-3 border-t border-grey-300 p-4">
      <button type="button" class="btn-inverse" [disabled]="drilldown.PageNumber <= 1" (click)="previousPage()">Previous</button>
      <span class="text-sm text-grey-900">Page {{drilldown.PageNumber}} of {{totalPages()}}</span>
      <button type="button" class="btn-inverse" [disabled]="drilldown.PageNumber >= totalPages()" (click)="nextPage()">Next</button>
    </div>
  }
</aside>
